import React from 'react';

import { FormattedMessage } from '../../../util/reactIntl';
import { formatMoney } from '../../../util/currency';
import { resolveCategoryLabel } from '../../../util/listingData';
import { isTermFormat } from '../../../util/createClass';

import { nextAvailableSlot, listingTimeZone, classBookableFromMs } from './classDetailData';

import { NamedLink } from '../../../components';

import css from './ClassDetail.module.css';
import { showsAsTerm, termWeeks } from '../../../util/termSchedule';

/**
 * The header band under the hero: title, "with {provider}", category + capacity chips, and the
 * price card with the Book now CTA. The spots-left urgency line is REAL data -- the seats remaining
 * on the next bookable time slot (single-session; a term shows its window instead, on the schedule
 * card). Ratings are intentionally absent: no ratings data exists.
 */
const ClassHeader = props => {
  const {
    listing,
    config,
    intl,
    authorDisplayName,
    monthlyTimeSlots,
    onBookNow,
    enrollmentClosed = false,
  } = props;

  const { title, price, publicData = {} } = listing.attributes || {};
  const authorId = listing.author?.id?.uuid || null;
  const { capacity, classFormat } = publicData;
  const isTerm = isTermFormat(classFormat);
  // Labels only: a one-session term (the old one-off workaround) is priced like a drop-in.
  const showTerm = showsAsTerm(publicData);

  const categoryLabel = resolveCategoryLabel(publicData, config.categoryConfiguration);
  const priceFormatted = price && price.amount != null ? formatMoney(intl, price) : null;

  const next = !isTerm
    ? nextAvailableSlot(monthlyTimeSlots, new Date(), classBookableFromMs(listing))
    : null;
  const timeZone = listingTimeZone(listing);
  const spotsLine =
    next && next.seats != null
      ? intl.formatMessage(
          { id: 'ClassDetailPage.spotsLeftOn' },
          {
            count: next.seats,
            day: intl.formatDate(next.start, {
              weekday: 'long',
              ...(timeZone ? { timeZone } : {}),
            }),
          }
        )
      : null;

  return (
    <div className={css.header}>
      <div className={css.headerInfo}>
        <h1 className={css.title}>{title}</h1>
        {authorDisplayName ? (
          <p className={css.withProvider}>
            <FormattedMessage
              id="ClassDetailPage.withProvider"
              values={{
                provider: authorId ? (
                  <NamedLink
                    className={css.providerLink}
                    name="ProfilePage"
                    params={{ id: authorId }}
                  >
                    {authorDisplayName}
                  </NamedLink>
                ) : (
                  authorDisplayName
                ),
              }}
            />
          </p>
        ) : null}
        <div className={css.chipRow}>
          {/* The format, as on the cards: teal Drop-in (a one-session term included) or yellow
              Multi-week. */}
          {classFormat === 'single-session' || isTerm ? (
            <span className={showTerm ? css.formatChip : css.formatChipDropIn}>
              <FormattedMessage
                id={showTerm ? 'ListingCard.formatMultiWeek' : 'ListingCard.formatDropIn'}
              />
            </span>
          ) : null}
          {categoryLabel ? <span className={css.categoryChip}>{categoryLabel}</span> : null}
          {Number.isInteger(capacity) ? (
            <span className={css.capacityChip}>
              <FormattedMessage id="ClassDetailPage.capacityChip" values={{ capacity }} />
            </span>
          ) : null}
        </div>
      </div>

      <div className={css.headerActions}>
        {priceFormatted ? (
          <div className={css.priceCard}>
            <span className={css.priceAmount}>{priceFormatted}</span>
            <span className={css.priceUnit}>
              {showTerm && termWeeks(listing?.attributes?.publicData) ? (
                <FormattedMessage
                  id="ClassDetailPage.priceUnitTermWeeks"
                  values={{ weeks: termWeeks(listing?.attributes?.publicData) }}
                />
              ) : (
                <FormattedMessage
                  id={showTerm ? 'ClassDetailPage.priceUnitTerm' : 'ClassDetailPage.priceUnitSession'}
                />
              )}
            </span>
            {spotsLine ? <span className={css.spotsLine}>{spotsLine}</span> : null}
          </div>
        ) : null}
        {onBookNow ? (
          <button type="button" className={css.bookNow} onClick={onBookNow}>
            <FormattedMessage id="ClassDetailPage.bookNow" />
          </button>
        ) : enrollmentClosed ? (
          <p className={css.enrollmentClosed}>
            <FormattedMessage id="ClassDetailPage.termEnrollmentClosed" />
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default ClassHeader;
